import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition, offsetLimitPagination } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { useMemo } from 'react'

import logger from '@/utils/logger'

import { WEBSITE_SETTINGS } from '@/client/settings'

import type { HasuraContextType } from '../internal'

// NOTE: You can turn on query logging for Hasura to debug unexpected querying behavior
const ENABLE_HASURA_LOGGING = false

function getHasuraApolloClient({ token }: { token: string | null }): ApolloClient<unknown> | null {
  // Primary HTTP link - used for mutations (and queries if no read-only endpoint)
  const primaryHttpLink = new HttpLink({
    uri: WEBSITE_SETTINGS.hasuraHttpUrl,
  })

  // Read-only HTTP link - used for queries (if configured)
  const readOnlyHttpUrl = WEBSITE_SETTINGS.hasuraReadOnlyHttpUrl
  const readOnlyHttpLink =
    readOnlyHttpUrl != null && readOnlyHttpUrl !== ''
      ? new HttpLink({
          uri: readOnlyHttpUrl,
        })
      : null

  // Split HTTP traffic: mutations go to primary, queries go to read-only (if configured)
  const httpLink = readOnlyHttpLink
    ? split(
        ({ query }) => {
          const def = getMainDefinition(query)
          return def.kind === 'OperationDefinition' && def.operation === 'mutation'
        },
        primaryHttpLink, // mutations -> primary
        readOnlyHttpLink // queries -> read-only
      )
    : primaryHttpLink

  const websocketLink =
    typeof globalThis.window !== 'undefined'
      ? new GraphQLWsLink(
          createClient({
            url: WEBSITE_SETTINGS.hasuraWsUrl,
            connectionParams:
              token != null
                ? {
                    headers: {
                      authorization: `Bearer ${token}`,
                    },
                  }
                : {},
          })
        )
      : null

  // Final network link: subscriptions go to WebSocket, everything else goes through httpLink
  const networkLink = split(
    ({ query }) => {
      const def = getMainDefinition(query)
      return def.kind === 'OperationDefinition' && def.operation === 'subscription'
    },
    websocketLink ?? httpLink,
    httpLink
  )

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      ...(token != null ? { authorization: `Bearer ${token}` } : {}),
    },
  }))

  const logLink = new ApolloLink((operation, forward) => {
    if (ENABLE_HASURA_LOGGING) {
      logger.debug('Hasura request', { operation })
    }
    return forward(operation)
  })

  const client = new ApolloClient({
    link: ApolloLink.from([authLink, logLink, networkLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            post: offsetLimitPagination(['where', 'currentUserId']),
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  })

  return client
}

export default function useHasuraContextProviderValue({
  token,
}: {
  token: string | null
}): HasuraContextType | null {
  const hasuraApolloClient = useMemo(() => getHasuraApolloClient({ token }), [token])

  if (hasuraApolloClient == null) {
    return null
  }

  return { apollo: hasuraApolloClient }
}
